import { type ChangeEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  SoftDeleteManualCfrSigningSessionValidation,
  useSoftDeleteManualCfrSigningSession,
} from "@/lib/api/cfr";
import { getMessageOrDefault } from "@/lib/prelude";

export type ManualCfrDeleteDialogProps = {
  open: boolean;
  cfrDocumentInstanceId: string | null;
  onOpenChange: (open: boolean) => void;
};

export function ManualCfrDeleteDialog({
  open,
  cfrDocumentInstanceId,
  onOpenChange,
}: ManualCfrDeleteDialogProps) {
  const { toast } = useToast();
  const softDeleteManualCfrSigningSession =
    useSoftDeleteManualCfrSigningSession();
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const closeAndReset = () => {
    setReason("");
    setErrorMessage("");
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isDeleting) {
      return;
    }
    if (!nextOpen) {
      closeAndReset();
      return;
    }
    onOpenChange(true);
  };

  const handleDelete = async () => {
    const requestResult = SoftDeleteManualCfrSigningSessionValidation.safeParse({
      cfr_document_instance_id: cfrDocumentInstanceId,
      reason,
    });
    if (!requestResult.success) {
      setErrorMessage(
        requestResult.error.issues[0]?.message || "A deletion reason is required",
      );
      return;
    }

    setIsDeleting(true);
    setErrorMessage("");
    try {
      const result = await softDeleteManualCfrSigningSession(requestResult.data);
      if (!result.success) {
        setErrorMessage(result.message);
        return;
      }

      toast({
        title: "Manual CFR deleted",
        description: "The manual CFR was removed.",
      });
      closeAndReset();
    } catch (error: unknown) {
      setErrorMessage(
        getMessageOrDefault(error, "Failed to delete the manual CFR")
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onEscapeKeyDown={(event) => {
          if (isDeleting) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isDeleting) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete manual CFR</DialogTitle>
          <DialogDescription>
            Archive the manual CFR. It cannot be edited after confirmation.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-4">
          <Label htmlFor="manual-cfr-delete-reason">Reason *</Label>
          <Textarea
            id="manual-cfr-delete-reason"
            value={reason}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              setReason(event.target.value);
              setErrorMessage("");
            }}
            disabled={isDeleting}
            placeholder="Explain why this manual CFR should be removed"
          />
        </div>

        {errorMessage && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {errorMessage}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={closeAndReset}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete manual CFR"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
